import { createReducer, on } from '@ngrx/store';

import {
  ChargerOperationalStatusStoreModelEnum,
  SiteAlertStateStoreModelEnum,
  SiteReadinessStateStoreModelEnum,
  SitesSortByStoreModelEnum,
  SitesViewModeStoreModelEnum,
} from '@domain/sites/sites.enums';
import { SiteStatsStoreModel, SitesFilterStoreModel } from '@domain/sites/sites.models';

import { SortDirectionStoreModelEnum } from '@shared/enums/sort-direction.enum';
import { areArraysEqual } from '@shared/utils/are-arrays-equal';
import { sortSitesByName } from '@shared/utils/sort-utils';
import { StoreToStorageSync } from '@shared/utils/store-to-storage-sync';

import { SitesTableSortPropertyStoreModelEnum } from '../enums/sites-table-sort-property.enum';
import {
  applyChargerStateFilterFromMap,
  applyStatusFilterFromMap,
  clearSites,
  detailedListOfSitesSocketMessageArrive,
  loadSites,
  loadSitesFailure,
  loadSitesSuccess,
  setMobileSitesSearch,
  setMobileSitesTenants,
  setSelectedSiteId,
  setSitesFilters,
  setSitesSortBy,
  setSitesTableSort,
  setSitesViewMode,
  toggleMapCollapsed,
  toggleSiteCollapsed,
  validateMobileSitesTenants,
} from './sites.actions';

export interface SitesState {
  sites?: SiteStatsStoreModel[];
  sitesIsLoading?: boolean;
  sitesError?: string;
  currentSitesFiltersValue?: SitesFilterStoreModel;
  mobileSearch: string;
  mobileSelectedTenants?: string[];
  viewMode: SitesViewModeStoreModelEnum;
  sitesSortBy: SitesSortByStoreModelEnum;
  sitesTableSortProperty: SitesTableSortPropertyStoreModelEnum;
  sitesTableSortDirection: SortDirectionStoreModelEnum;
  sitesCollapsedById?: Record<string, boolean>;
  selectedSiteId?: string;
  mapCollapsed: boolean;
}

const storageData = StoreToStorageSync.getStorageData();

export const initialState: SitesState = {
  mobileSearch: '',
  mobileSelectedTenants: storageData?.mobileSitesSelectedTenants,
  viewMode: storageData?.sitesViewMode ?? SitesViewModeStoreModelEnum.Cosy,
  sitesSortBy: SitesSortByStoreModelEnum.AlphabeticallyAscending,
  sitesTableSortDirection: SortDirectionStoreModelEnum.Asc,
  sitesTableSortProperty: SitesTableSortPropertyStoreModelEnum.SiteName,
  mapCollapsed: storageData?.sitesMapCollapsed ?? false,
};

export const sitesFeatureKey = 'sites';

export const sitesReducer = createReducer(
  initialState,
  on(loadSites, (state, { polling }): SitesState => ({ ...state, sitesIsLoading: !polling, sitesError: undefined })),
  on(loadSitesSuccess, (state, { sites }): SitesState => {
    const sortedSites = sortSitesByName(sites);
    let existingFilters = state.currentSitesFiltersValue;
    let mobileSelectedTenants = state.mobileSelectedTenants;

    if (!state.sites) {
      const allTenants = sortedSites.reduce((acc, site) => {
        if (site.tenant && !acc.includes(site.tenant)) {
          acc.push(site.tenant);
        }
        return acc;
      }, [] as string[]);

      const allRegions = sortedSites.reduce((acc, site) => {
        if (site.region && !acc.includes(site.region)) {
          acc.push(site.region);
        }
        return acc;
      }, [] as string[]);

      const tenants = allTenants;

      const regions = allRegions;

      existingFilters = {
        search: existingFilters?.search ?? '',
        tenants: existingFilters?.tenants ?? tenants,
        regions,
        alertStates: Object.values(SiteAlertStateStoreModelEnum),
        chargerStates: Object.values(ChargerOperationalStatusStoreModelEnum),
        readinessStates: Object.values(SiteReadinessStateStoreModelEnum),
      };

      const filteredMobileSelectedTenants = mobileSelectedTenants?.filter((tenant) => tenants.includes(tenant)) ?? allTenants;

      if (!areArraysEqual(mobileSelectedTenants, filteredMobileSelectedTenants)) {
        StoreToStorageSync.setStorageData({
          mobileSitesSelectedTenants: filteredMobileSelectedTenants,
        });
        mobileSelectedTenants = filteredMobileSelectedTenants;
      }
    }

    const existingSitesCollapsedByIdIsCompatibleWithNewSites = sortedSites.every((site) =>
      state.sitesCollapsedById?.hasOwnProperty(site.id)
    );

    return {
      ...state,
      sitesIsLoading: false,
      sites: sortedSites,
      currentSitesFiltersValue: existingFilters,
      mobileSelectedTenants,
      sitesCollapsedById: existingSitesCollapsedByIdIsCompatibleWithNewSites
        ? state.sitesCollapsedById
        : sortedSites.reduce(
            (acc, site) => {
              acc[site.id] = state.viewMode === SitesViewModeStoreModelEnum.Cosy;
              return acc;
            },
            {} as Record<string, boolean>
          ),
    };
  }),
  on(
    loadSitesFailure,
    (state, { error }): SitesState => ({
      ...state,
      sitesIsLoading: false,
      sitesError: error,
    })
  ),
  on(detailedListOfSitesSocketMessageArrive, (state, { sites }): SitesState => {
    let newSites = state.sites ?? [];

    sites.forEach((newSite) => {
      const existingSiteIndex = newSites.findIndex((existingSite) => existingSite.id === newSite.id);
      newSites =
        existingSiteIndex === -1
          ? [...newSites, newSite]
          : newSites.map((existingSite, index) =>
              index === existingSiteIndex
                ? {
                    ...existingSite,
                    ...newSite,
                    // TODO remove these safety copies when BE response does not miss them
                    alertState: newSite.alertState ?? existingSite.alertState,
                    readinessState: newSite.readinessState ?? existingSite.readinessState,
                    predictedDeparture: newSite.predictedDeparture ?? existingSite.predictedDeparture,
                    predictedDepartureHealthGrade: newSite.predictedDepartureHealthGrade ?? existingSite.predictedDepartureHealthGrade,
                    performancePercentage: newSite.performancePercentage ?? existingSite.performancePercentage,
                    criticalErrorsCount: newSite.criticalErrorsCount ?? existingSite.criticalErrorsCount,
                    majorErrorsCount: newSite.majorErrorsCount ?? existingSite.majorErrorsCount,
                  }
                : existingSite
            );
    });

    return {
      ...state,
      sites: sortSitesByName(newSites),
    };
  }),
  on(setSitesFilters, (state, { filters }): SitesState => {
    const newFilters = { ...state.currentSitesFiltersValue!, ...filters };

    return {
      ...state,
      currentSitesFiltersValue: newFilters,
    };
  }),
  on(
    setMobileSitesSearch,
    (state, { search }): SitesState => ({
      ...state,
      mobileSearch: search,
    })
  ),
  on(setMobileSitesTenants, (state, { tenants }): SitesState => {
    StoreToStorageSync.setStorageData({
      mobileSitesSelectedTenants: tenants,
    });

    return {
      ...state,
      mobileSelectedTenants: tenants,
    };
  }),
  on(validateMobileSitesTenants, (state, { availableTenants }): SitesState => {
    const mobileSelectedTenants = state.mobileSelectedTenants?.filter((tenant) => availableTenants.includes(tenant)) ?? availableTenants;

    if (areArraysEqual(state.mobileSelectedTenants, mobileSelectedTenants)) {
      return state;
    }

    StoreToStorageSync.setStorageData({
      mobileSitesSelectedTenants: mobileSelectedTenants,
    });

    return {
      ...state,
      mobileSelectedTenants,
    };
  }),
  on(
    setSitesSortBy,
    (state, { sortBy }): SitesState => ({
      ...state,
      sitesSortBy: sortBy,
    })
  ),
  on(toggleSiteCollapsed, (state, { siteId }): SitesState => {
    return {
      ...state,
      sitesCollapsedById: {
        ...state.sitesCollapsedById!,
        [siteId]: !state.sitesCollapsedById![siteId],
      },
    };
  }),
  on(setSitesViewMode, (state, { viewMode }): SitesState => {
    if (state.viewMode === viewMode) {
      return state;
    }

    StoreToStorageSync.setStorageData({
      sitesViewMode: viewMode,
    });

    return {
      ...state,
      viewMode,
      sitesCollapsedById: state.sites?.reduce(
        (acc, site) => {
          acc[site.id] = viewMode === SitesViewModeStoreModelEnum.Cosy;
          return acc;
        },
        {} as Record<string, boolean>
      ),
    };
  }),
  on(
    setSelectedSiteId,
    (state, { siteId }): SitesState => ({
      ...state,
      selectedSiteId: siteId ?? undefined,
    })
  ),
  on(applyStatusFilterFromMap, (state, { status }): SitesState => {
    const newFilters = {
      ...state.currentSitesFiltersValue!,
      alertStates: [status],
    };

    return {
      ...state,
      currentSitesFiltersValue: newFilters,
    };
  }),
  on(applyChargerStateFilterFromMap, (state, { chargerState }): SitesState => {
    const newFilters = {
      ...state.currentSitesFiltersValue!,
      chargerStates: [chargerState],
    };

    return {
      ...state,
      currentSitesFiltersValue: newFilters,
    };
  }),
  on(
    setSitesTableSort,
    (state, { sortProperty, sortDirection }): SitesState => ({
      ...state,
      sitesTableSortProperty: sortProperty,
      sitesTableSortDirection: sortDirection,
    })
  ),
  on(toggleMapCollapsed, (state): SitesState => {
    const newCollapsed = !state.mapCollapsed;

    StoreToStorageSync.setStorageData({
      sitesMapCollapsed: newCollapsed,
    });

    return {
      ...state,
      mapCollapsed: newCollapsed,
    };
  }),
  on(
    clearSites,
    (state): SitesState => ({
      ...initialState,
      mobileSelectedTenants: state.mobileSelectedTenants,
    })
  )
);
